import './Marche.css';
import React, { useRef, useEffect, useState } from 'react';
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';
import DocumentsList from '../DocumentsList/DocumentsList';
import { isPrestataireRole, isClientRole } from '../../utils/roleUtils';
import { useAuth } from '../../contexts/AuthContext';
// YOUSIGN - Commenté pour réactivation future
// import YousignIframe from '../YousignIframe/YousignIframe';
import Button from '../Button/Button';
import MailPreview from '../MailPreview/MailPreview';
import Coordonnees from '../Coordonnees/Coordonnees';
import { PrintButton } from '../PrintButton/PrintButton';
import { formatDate, getStatusColor } from '../../utils/entityUtils';
import { getLotImage } from '../../utils/getLotImage';


interface MarcheProps {
  operation?: any;
  commande?: any;
  selectedPrestataire: any;
  printRef?: any;
}

interface ModalProps {
  children: React.ReactNode;
  isOpen: boolean;
  handleClose: () => void;
  closeButton?: boolean;
}

const Modal: React.FC<ModalProps> = ({ children, isOpen, handleClose, closeButton }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!modalRef.current || !closeButton) return;
    if (event.target === modalRef.current) {
      handleClose();
    }
  };

  // Bloquer le scroll du body quand la modal est ouverte
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <div
      ref={modalRef}
      className={`ys-modal-overlay ${isOpen ? 'open' : ''}`}
      onClick={handleClickOutside}
      style={{
        display: isOpen ? 'flex' : 'none',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
    >
      <div
        className="ys-modal-content"
        style={{
          position: 'relative',
          background: '#fff',
          borderRadius: '8px',
          maxHeight: '90vh',
          height: '90vh',
          overflowY: 'auto',
          width: '100%',
          maxWidth: '80vw',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {children}
        </div>
        {closeButton && (
          <Button
            type="button"
            onClick={handleClose}
            value="Fermer"
            size="small"
            style={{ position: 'absolute', top: 10, left: 10 }}
          />
        )}
      </div>
    </div>
  );
};


const Marche: React.FC<MarcheProps> = ({ operation, commande, selectedPrestataire, printRef }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedCommandeForPreview, setSelectedCommandeForPreview] = useState<any>(null);
  const { user } = useAuth();
  // const contentRef = useRef<HTMLDivElement>(null);
  const isPrestataireView = isPrestataireRole(user?.role || '');
  const [signing, setSigning] = useState(false);

  // États pour la demande de signature
  const [acheteurSignataires, setAcheteurSignataires] = useState<any[]>([]);
  const [selectedSignataireId, setSelectedSignataireId] = useState<number | null>(null);
  const [loadingSignataires, setLoadingSignataires] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);

  // État pour la prévisualisation du PDF du MS
  const [msPdfBlobUrl, setMsPdfBlobUrl] = useState<string | null>(null);
  const [loadingMsPdf, setLoadingMsPdf] = useState(false);

  // SUPPRIMÉ: L'ancien useEffect fetchDocuments qui appelait /documents/numero (table documents obsolète)
  // La nouvelle architecture utilise documents_accord_cadre_meta et est gérée dans fetchMsPdf ci-dessous

  // Récupérer le PDF du MS pour la prévisualisation
  // Architecture : documents_accord_cadre_meta (type=numéro) -> documents_bin
  // Utilise downloadSignedContratDocument qui cherche directement par type (numéro MS)
  useEffect(() => {
    const fetchMsPdf = async () => {
      const numero = operation?.numero ?? commande?.operation?.numero;
      if (!numero) return;

      // Récupérer le numéro sans le préfixe 'P' si présent
      const numeroSansP = numero.startsWith('P') ? numero.substring(1) : numero;

      setLoadingMsPdf(true);
      try {
        // Utiliser downloadSignedContratDocument qui cherche directement le document MS
        // par son type (numéro) dans documents_accord_cadre_meta -> documents_bin
        // Note: listDocumentsAccordCadre filtre uniquement 'contrat%' et 'cv_equipe', 
        // donc ne retourne pas les documents MS
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/documents/signed-contrat`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              external_id: numeroSansP,
              external_type: 'operation',
              source: 'MS',
            }),
          }
        );

        if (!response.ok) {
          console.warn('Document MS non trouvé pour la prévisualisation');
          return;
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setMsPdfBlobUrl(blobUrl);
      } catch (err) {
        console.error('Erreur chargement PDF MS:', err);
        setMsPdfBlobUrl(null);
      } finally {
        setLoadingMsPdf(false);
      }
    };

    fetchMsPdf();

    // Cleanup: libérer l'URL blob
    return () => {
      if (msPdfBlobUrl) {
        URL.revokeObjectURL(msPdfBlobUrl);
      }
    };
  }, [operation?.numero, commande?.operation?.numero]);

  // Récupérer les signataires acheteur de l'organisme pour la demande de signature
  useEffect(() => {
    const fetchAcheteurSignataires = async () => {
      const organismeId = operation?.organisme?.id;
      if (!organismeId) return;

      setLoadingSignataires(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/organismes/${organismeId}/signataires`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        });

        if (!res.ok) throw new Error('Erreur lors de la récupération des signataires');

        const json = await res.json();
        // Filtrer pour ne garder que les validateurs et responsables achat
        const signataires = json.data || [];
        setAcheteurSignataires(signataires);
        console.log('Signataires acheteur récupérés:', signataires);
      } catch (err) {
        console.error('Erreur récupération signataires:', err);
        setAcheteurSignataires([]);
      } finally {
        setLoadingSignataires(false);
      }
    };

    fetchAcheteurSignataires();
  }, [operation?.organisme?.id]);

  // Fonction pour envoyer la demande de signature par mail
  const handleRequestSignature = async () => {
    if (!selectedSignataireId) {
      alert("Veuillez sélectionner un signataire.");
      return;
    }

    const selectedSignataire = acheteurSignataires.find(s => s.id === selectedSignataireId);
    if (!selectedSignataire) {
      alert("Signataire non trouvé.");
      return;
    }

    setSendingRequest(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/request-signature`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation_id: operation?.id,
          signataire_id: selectedSignataireId,
          signataire_type: selectedSignataire.type_signataire,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erreur lors de l\'envoi de la demande');
      }

      alert('Demande de signature envoyée avec succès !');
      window.location.reload();
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi de la demande:', error);
      alert(error.message || 'Impossible d\'envoyer la demande de signature.');
    } finally {
      setSendingRequest(false);
    }
  };

  // Vérifier si l'utilisateur est un acheteur simple (pas signataire)
  const isAcheteurSimple = () => {
    const role = user?.role?.toLowerCase() || '';
    return role === 'acheteur' && !role.includes('signataire');
  };

  // Vérifier si l'utilisateur est un acheteur signataire (admin ou pas)
  const isAcheteurSignataire = () => {
    const role = user?.role?.toLowerCase() || '';
    return role.includes('acheteur signataire') || role.includes('responsable_achat') || role.includes('validateur');
  };

  // YOUSIGN - Commenté pour réactivation future
  // const handleSign = async (
  //   operation: any,
  //   id_signataire?: string,
  //   type_signataire_prestataire?: string
  // ) => {
  //   try {
  //     if (!operation || !operation.commandes || operation.commandes.length === 0) {
  //       alert("Aucune commande disponible pour la signature.");
  //       return;
  //     }

  //     const selectedCommande = operation?.commandes?.find(c => c.id === operation?.id_commande);
  //     if (!selectedCommande) {
  //       alert("Impossible de trouver la commande pour la signature.");
  //       return;
  //     }

  //     const prestataireId = selectedCommande.id_signataire;
  //     const acheteurId = operation.id_signataire ?? id_signataire;
  //     const typePrestataire = selectedCommande.type_signataire || '';
  //     const typeAcheteur = (operation.type_signataire ?? type_signataire_prestataire) || '';

  //     if (!prestataireId || !acheteurId) {
  //       alert("Impossible de récupérer les identifiants du signataire ou du prestataire.");
  //       console.error({ prestataireId, acheteurId });
  //       return;
  //     }

  //     setSigning(true); // ⚡ On bloque le bouton

  //     const formData = new FormData();
  //     formData.append("acheteur_signataire_id", acheteurId.toString());
  //     formData.append("prestataire_signataire_id", prestataireId.toString());
  //     formData.append("type_signataire_prestataire", typePrestataire);
  //     formData.append("type_signataire_acheteur", typeAcheteur);
  //     formData.append("name", `Contrat ${operation.nom}`);
  //     formData.append("expiration_date", selectedCommande.expire_at.split(' ')[0] || '');
  //     formData.append("external_type", "operation");
  //     formData.append("external_id", operation.id.toString());
  //     formData.append("external_id_prestataire", selectedCommande?.id?.toString());
  //     formData.append("external_type_prestataire", "commande");

  //     const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/sign`, {
  //       method: "POST",
  //       headers: {
  //         Authorization: `Bearer ${localStorage.getItem("token")}`,
  //       },
  //       body: formData,
  //     });

  //     if (!response.ok) throw new Error("Erreur lors de la création de la signature");

  //     const data = await response.json();
  //     console.log("Signature créée :", data);

  //     // ✅ Si tout est OK, reload
  //     window.location.reload();

  //   } catch (error) {
  //     console.error("Erreur lors de la signature :", error);
  //     alert("Impossible de créer la demande de signature.");
  //   } finally {
  //     setSigning(false); // ⚡ Débloque le bouton si erreur
  //   }
  // };

  /**
   * Signature côté public (organisme) - remplace YouSign
   * Appelé par responsable_achat ou validateur
   */
  const handleSignPublic = async (operation: any) => {
    try {
      if (!operation || !operation.commandes || operation.commandes.length === 0) {
        alert("Aucune commande disponible pour la signature.");
        return;
      }

      const selectedCommande = operation?.commandes?.find((c: any) => c.id === operation?.id_commande);
      if (!selectedCommande) {
        alert("Impossible de trouver la commande pour la signature.");
        return;
      }

      setSigning(true);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/public/sign/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation_id: operation.id,
          commande_id: selectedCommande.id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Erreur lors de la signature");
      }

      console.log("Signature publique effectuée :", data);
      alert("Signature côté organisme effectuée avec succès !");
      window.location.reload();

    } catch (error: any) {
      console.error("Erreur lors de la signature publique :", error);
      alert(error.message || "Impossible de signer le marché.");
    } finally {
      setSigning(false);
    }
  };

  /**
   * Signature côté privé (prestataire) - remplace YouSign
   * Appelé par signataire ou gestionnaire
   */
  const handleSignPrivate = async (operation: any, commande: any) => {
    try {
      if (!operation || !commande) {
        alert("Données de l'opération ou de la commande manquantes.");
        return;
      }

      setSigning(true);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/private/sign/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation_id: operation.id,
          commande_id: commande.id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Erreur lors de la signature");
      }

      console.log("Signature privée effectuée :", data);
      alert("Signature côté prestataire effectuée avec succès ! Le marché est maintenant signé.");
      window.location.reload();

    } catch (error: any) {
      console.error("Erreur lors de la signature privée :", error);
      alert(error.message || "Impossible de signer le marché.");
    } finally {
      setSigning(false);
    }
  };

  /**
   * Envoi du document au prestataire pour signature (nouvelle logique simple)
   * Remplace l'ancien handleSign qui utilisait YouSign
   */
  const handleSendToPrestataire = async (operation: any) => {
    try {
      if (!operation || !operation.commandes || operation.commandes.length === 0) {
        alert("Aucune commande disponible.");
        return;
      }

      const selectedCommande = operation?.commandes?.find((c: any) => c.id === operation?.id_commande);
      if (!selectedCommande) {
        alert("Impossible de trouver la commande.");
        return;
      }

      setSigning(true);

      // Appeler signPublic pour mettre à jour le statut et envoyer au prestataire
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/public/sign/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation_id: operation.id,
          commande_id: selectedCommande.id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Erreur lors de la signature");
      }

      console.log("Marché signé et prestataire notifié :", data);
      alert("Marché subséquent signé avec succès ! Le prestataire retenu a été notifié.");
      window.location.reload();

    } catch (error: any) {
      console.error("Erreur lors de la signature :", error);
      alert(error.message || "Impossible de signer le marché.");
    } finally {
      setSigning(false);
    }
  };


  const handlePrintMail = async (commande) => {
    const htmlContent = generateMailPreview(commande);

    // Si Marche.css est buildé et accessible, on peut le fetcher
    const response = await fetch('/static/css/Marche.css'); // ou le chemin réel dans ton build
    const marcheCSS = await response.text();

    // Créer l'iframe invisible
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(`
      <html>
        <head>
          <meta charset="utf-8" />
          <style>${marcheCSS}</style> <!-- CSS injecté -->
        </head>
        <body>${htmlContent}</body>
      </html>
    `);
      doc.close();

      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();

        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 100);
      };
    }
  };

  const handleClose = () => setIsOpen(false);
  const handleClick = () => setIsOpen(true);

  const handlePreviewMessage = (commande) => {
    setSelectedCommandeForPreview(commande);
    setIsPreviewOpen(true);
  };

  const isPresta = isPrestataireRole(user?.role ?? 'null');
  const isAcheteur = isClientRole(user?.role ?? 'null');

  const operationStatus = operation?.status?.id ?? null;
  const commandeStatus = commande?.status?.id ?? null;
  const [operationData, setOperationData] = useState(operation);

  const isNotified = (commande: any) => {
    return operationData?.historique?.some(h =>
      h.prefix === 'Notification' &&
      h.destinataire === commande.id.toString()
    );
  };

  const handleNotifyPrestataire = async (
    operationId: number,
    commandeId: number,
    selectedCommandeId: number
  ) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/subsequents/notifications/send`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            operation_id: operationId,
            commande_id: commandeId,
            selected_commande_id: selectedCommandeId
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erreur lors de la notification');
      }

      // Mettre à jour l'historique localement pour le rendu immédiat
      if (operation?.historique) {
        const newEntry = {
          prefix: 'Notification',
          label: selectedCommandeId === commandeId
            ? `Notification au retenu pour la commande N° ${commandeId} pour l'opération ${operationId}`
            : `Notification au non retenu pour la commande N° ${commandeId} pour l'opération ${operationId}`,
          destinataire: commandeId.toString(),
          created_at: new Date().toISOString(),
        };
        operation.historique.push(newEntry);
        // Forcer le rerender
        setOperationData({ ...operation });
      }

    } catch (error: any) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue lors de la notification');
    }
  };

  useEffect(() => {
    if (isAcheteur) console.log('operation:', operation);
    else console.log('commande:', commande);
  }, [isAcheteur, operation, commande]);

  const external_id = isAcheteur
    ? operation?.id?.toString()
    : isPresta
      ? commande?.id?.toString()
      : '';

  const prestaAdress = selectedPrestataire?.contrat?.entite?.adresse
    ? `${selectedPrestataire?.contrat?.entite?.adresse?.numero_voie || ''} ${selectedPrestataire?.contrat?.entite?.adresse?.type_voie || ''} ${selectedPrestataire?.contrat?.entite?.adresse?.nom_voie || ''}, ${selectedPrestataire?.contrat?.entite?.adresse?.code_postal || ''} ${selectedPrestataire?.contrat?.entite?.adresse?.ville || ''} ${selectedPrestataire?.contrat?.entite?.adresse?.arrondissement || ''}`
    : `${commande?.contrat?.entite?.adresse?.numero_voie || ''} ${commande?.contrat.entite?.adresse?.type_voie || ''} ${commande?.contrat?.entite?.adresse?.nom_voie || ''}, ${commande?.contrat?.entite?.adresse?.code_postal || ''} ${commande?.contrat?.entite?.adresse?.ville || ''} ${commande?.contrat?.entite?.adresse?.arrondissement || ''}`


  // Cas spécifiques avant le rendu principal
  if (isPresta && commandeStatus === 6) {
    return (
      <div className="tab-marche">
        <div className="marche-message">
          <h3>Marché refusé</h3>
        </div>
      </div>
    );
  }

  if (isPresta && commandeStatus === 5) {
    return (
      <div className="tab-marche">
        <div className="marche-message">
          <h3>En attente d'analyse</h3>
        </div>
      </div>
    );
  }
  console.log("isPresta:", isPresta, "commandeStatus:", commandeStatus);


  const handleDownloadSigned = async (operationParam?: any) => {
    try {
      // Priorité : operation passée en paramètre > commande.operation (pour prestataire)
      const numero = operationParam?.numero?.toString() ?? commande?.operation?.numero?.toString();

      if (!numero) {
        throw new Error('Numéro d\'opération non trouvé');
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/documents/signed-contrat`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/pdf'
          },
          body: JSON.stringify({
            external_id: numero,
            external_type: "operation",
            source: 'MS',
          })
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement du document signé');
      }

      // ✅ Conversion en blob (PDF)
      const blob = await response.blob();

      // ✅ Création d’un lien de téléchargement temporaire
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Nom du fichier (personnalisable)
      const annee = process.env.REACT_APP_ANNEE_EDITION || "25";
      const numeroSansP = operation?.numero ?? commande?.operation?.numero;
      a.download = `${annee}${numeroSansP} (signed).pdf`;
      // ✅ Déclenche le téléchargement
      document.body.appendChild(a);
      a.click();

      // ✅ Nettoyage
      a.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue lors du téléchargement du marché signé');
    }
  };

  const handleDownloadAuditTrail = async (operation: any) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/audit-trail`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          external_id: operation?.numero?.toString() ?? commande?.operation?.numero?.toString() ?? operation?.id?.toString(),
          external_type: "operation"
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erreur backend:', errorText);
        throw new Error('Erreur lors du téléchargement');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `preuves_signatures_${operation?.numero ?? commande?.operation?.numero}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

    } catch (error: any) {
      console.error(error);
      alert(error.message || 'Impossible de télécharger l’audit trail');
    }
  };


  const generateMailPreview = (commande) => {
    const isRetenuMail = operation?.id_commande === commande?.id;
    const acheteurNom = operation?.organisme?.nom || "NOM_ACHETEUR";
    const operationNom = operation?.nom || "Nom de l'opération";
    const numero = commande?.numero || operation?.numero || "XXX";
    const date = new Date().toLocaleDateString('fr-FR');
    const montant = commande?.prix_total?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0,00";
    const interlocuteur = commande?.prestataire_signataire?.prenom + ' ' + commande?.prestataire_signataire?.nom || "INTERLOCUTEUR";
    const interlocuteurTel = commande?.prestataire_signataire?.telephone || "00 00 00 00 00";
    const noteTechnique = commande?.note_technique?.note_technique.toString() || "X";
    const noteFinanciere = commande?.note_financiere.toString() || "X";
    const noteGlobale = ((noteTechnique * 0.55) + (noteFinanciere * 0.45)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "X";
    const bestPrestataire = operationData.commandes.find(c => c.id_status === 8);
    const bestPrestaName = bestPrestataire?.contrat?.entite?.nom || "Entreprise retenue";
    const bestPrestaNoteTechnique = bestPrestataire?.note_technique?.note_technique.toString() || "X";
    const bestPrestaNoteFinanciere = bestPrestataire?.note_financiere.toString() || "X";
    const bestPrestaNoteGlobale = ((bestPrestaNoteTechnique * 0.55) + (bestPrestaNoteFinanciere * 0.45)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "X";
    // au format jj/mm/aaaa à hh:mm:ss
    const dateExecution = operation?.date_debut_operation
      ? `${new Date(operation.date_debut_operation).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })} à ${new Date(operation.date_debut_operation).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })}`
      : "JJ/MM/AAAA à HH:MM:SS";
    if (isRetenuMail) {
      return `
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      body {
        font-family: "Segoe UI", Roboto, Arial, sans-serif;
        background-color: #f7f9fb;
        padding: 40px;
        color: #333;
        line-height: 1.6;
      }
      .mail-container {
        max-width: 700px;
        margin: 0 auto;
        background: #ffffff;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        padding: 40px 50px;
        display: flex;
        flex-direction: column;
      }
      .header-container {
        max-width: 700px;
        margin: 0 auto;
        background: #ffffff;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        padding: 40px 50px;
      }
      h1 {
        font-size: 20px;
        margin-bottom: 25px;
        border-bottom: 1px solid #eee;
        padding-bottom: 10px;
      }
      h2 {
        background-color: #ffffff;
        max-width: 700px;
        margin: 0 auto 20px auto;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        padding: 20px 50px;
      }
      p {
        margin: 14px 0;
        font-size: 15px;
      }
      strong {
        color: #000;
      }
      .footer {
        margin-top: 40px;
        padding-top: 15px;
        border-top: 1px solid #eee;
        font-size: 14px;
        color: #666;
      }
      
      span {
        padding: 1rem 1.5rem;
        font-size: 1.1rem;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #da7027 !important;
        color: #fffdfc;
        border: none;
        border-radius: 50px;;
        font-weight: 700;
        transition: all 0.2s ease;
        display: inline-flex;
        align-items: center;
        text-decoration: none;
        margin: 1.5rem auto 0 auto !important;
      }
    </style>
  </head>
  <body>
    <div className="header-container">
      <h2>Aperçu du message de notification au retenu</h2>
    </div>
    <div class="mail-container">
      <h1 className="object">Objet : Notification du Marché subséquent N°${numero} pour l’opération ${operationNom} de l’acheteur ${acheteurNom}.</h1>

      <p>Cher prestataire,</p>

      <p>
        Par le présent mail, nous avons le plaisir de vous notifier le marché subséquent <strong>N°${numero}</strong> du <strong>${date}</strong> pour l’opération <strong>${operationNom}</strong>, d’un montant de <strong>${montant}€</strong>, et le rendons exécutoire à partir de ce jour</strong>.
      </p>

      <p>
        L’interlocuteur identifié est <strong>${interlocuteur}</strong> dont le N° de tél est <strong>${interlocuteurTel}</strong>. N’hésitez pas à le joindre.
      </p>

      <p>
        Vous pouvez retrouver l’intégralité du suivi concernant ce bon sur votre espace en ligne et vous rappelons qu’il vous appartient de maintenir à jour vos pièces telles qu’assurances, attestations, etc…
      </p>

        <span href="https://c.centralis-europe.com/commande-ms/${commande.id}" target="_blank">Voir le Marché subséquent</span>

      <div class="footer">
        <p>
          Cordialement,<br/>
          <strong>L’équipe Centralis</strong>
        </p>
      </div>
    </div>
  </body>
</html>
`;
    }

    // Non retenu
    return `
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      body {
        font-family: "Segoe UI", Roboto, Arial, sans-serif;
        background-color: #f7f9fb;
        padding: 40px;
        color: #333;
        line-height: 1.6;
      }
      .mail-container {
        max-width: 700px;
        margin: 0 auto;
        background: #ffffff;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        padding: 40px 50px;
      }
      .header-container {
        max-width: 700px;
        margin: 0 auto;
        background: #ffffff;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        padding: 40px 50px;
      }
      h1 {
        font-size: 20px;
        margin-bottom: 25px;
        border-bottom: 1px solid #eee;
        padding-bottom: 10px;
      }
      h2 {
        background-color: #ffffff;
        max-width: 700px;
        margin: 0 auto 20px auto;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        padding: 20px 50px;
      }
      p {
        margin: 14px 0;
        font-size: 15px;
      }
      strong {
        color: #000;
      }
      .footer {
        margin-top: 40px;
        padding-top: 15px;
        border-top: 1px solid #eee;
        font-size: 14px;
        color: #666;
      }
    </style>
  </head>
  <body>
    <div className="header-container">
      <h2>Aperçu du message de notification au non-retenu</h2>
    </div>
    <div class="mail-container">
      <h1 className="object">Objet : Offre non retenue pour la consultation de l’opération n°${numero} de la part de l’acheteur ${acheteurNom}.</h1>

      <p>Cher prestataire,</p>

      <p>
        Nous avons le regret de vous informer que votre offre n’a pas été retenue dans le cadre du marché subséquent pour
        l’opération ${numero} de la part de l’acheteur ${acheteurNom}.
      </p>

      <p>
        Nous portons à votre connaissance que l’entreprise suivante a été retenue :
        <strong>${operationData.commandes.find(c => c.id_status === 8)?.contrat?.entite?.nom || "Entreprise retenue"}</strong>
        avec une note de <strong>${bestPrestaNoteGlobale}/10</strong> (dont ${bestPrestaNoteTechnique}/10 pour la valeur technique,
        et ${bestPrestaNoteFinanciere}/10 pour la valeur financière).
      </p>

      <p>
        En ce qui concerne votre entreprise, vous avez obtenu la note de <strong>${noteGlobale}/10</strong>
        (dont ${noteTechnique}/10 pour la valeur technique, et ${noteFinanciere}/10 pour la valeur financière).
      </p>

      <p>
        L’acheteur et notre Centrale tenions à vous remercier de votre participation.
        Nous ne manquerons pas de revenir vers vous pour les prochaines consultations.
      </p>

      <div class="footer">
        <p>
          Cordialement,<br/>
          <strong>L’équipe Centralis</strong>
        </p>
      </div>
    </div>
  </body>
</html>
`;
  };

  const getDisplayStatus = () => {
    const opStatus = operation?.status;
    const cmdStatus = commande?.operation?.status;

    if (!opStatus && !cmdStatus) return 'Statut inconnu';

    const statusName = opStatus?.nom || cmdStatus?.nom;
    const statusId = opStatus?.id || cmdStatus?.id;

    if (isPrestataireView) {
      const rang = getRangPrestataire();
      if (statusName === `En attente de l'acceptation du rang ${rang}`) {
        return 'À accepter';
      }
    }

    return statusName || 'Statut inconnu';
  };

  // CORRECTION: Déterminer le rang du prestataire depuis les données API
  const getRangPrestataire = () => {
    if (!commande || !isPrestataireView) return 1;
    return commande.rang || 1;
  };

  // CORRECTION: Fonction pour formater le sous-titre
  const getSousTitre = () => {
    const numero = commande?.operation?.numero || operation?.numero;
    const rang = getRangPrestataire();
    return `Suivi du Marché Subséquent n° ${numero}`;
  };

  // CORRECTION: Fonction pour formater le subtitle (lot technique + géographique)
  const getSubtitle = () => {
    const lotTechnique = commande?.operation?.lot_technique || operation?.lot_technique;
    const departement = commande?.operation?.lot_departement?.departement || operation?.lot_departement?.departement;

    if (lotTechnique && departement) {
      return `${lotTechnique.id} - ${lotTechnique.nom}, ${departement.id} - ${departement.nom}`;
    }
    return 'Informations de lot non disponibles';
  };
  return (
    <>
      <div>
        <Modal isOpen={isPreviewOpen} handleClose={() => setIsPreviewOpen(false)} closeButton={false}>
          {selectedCommandeForPreview && (
            <div className="pdf-preview-container" style={{ position: 'relative', maxHeight: '200vh', overflowY: 'auto' }}>
              <div
                style={{
                  position: 'sticky',
                  top: '0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 20px',
                  background: 'rgba(255, 255, 255, 0.9)', // léger fond pour contraste
                  boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                  zIndex: 10,
                  gap: '1rem', // espace entre les éléments
                }}
              >
                <Button
                  value="Fermer"
                  variant="primary"
                  size="small"
                  onClick={() => setIsPreviewOpen(false)}
                  className="ys-modal-close-button-2"
                />

                {/* Message "Notifié le ..." */}
                {isNotified(selectedCommandeForPreview) && (
                  <span className="notification-sent">
                    Notifié le{" "}
                    {formatDate(new Date(
                      operationData.historique.find(
                        (h) =>
                          h.prefix === "Notification" &&
                          h.destinataire === selectedCommandeForPreview.id.toString()
                      )?.created_at
                    ))}
                  </span>
                )}

                <Button
                  value="PDF"
                  variant="secondary"
                  icon={<PdfIcon />}
                  size="small"
                  onClick={() => handlePrintMail(selectedCommandeForPreview)}
                />
              </div>

              {/* Aperçu du message */}
              <MailPreview
                selectedCommandeForPreview={selectedCommandeForPreview}
                generateMailPreview={generateMailPreview}
              />
            </div>
          )}
        </Modal>

        {/* Signature */}
        <div className="tab-marche">
          <section>
            <h3 className="section-header">Signature et notification au retenu</h3>
            <div className="section-content">
              {/* YOUSIGN - Commenté pour réactivation future */}
              {/* {((isAcheteur && operationStatus === 7 && user?.id.toString() === operation?.acheteur_signataire?.id_user?.toString()) ||
                (isPresta && commandeStatus === 10 && commande?.prestataire_signataire?.id_user?.toString() === user?.id?.toString())) && (
                  <div className="signature-pending">
                    <h4>Signature électronique du marché</h4>
                    <p>La signature électronique est réalisée via notre partenaire Yousign.</p>
                    <Modal isOpen={isOpen} handleClose={handleClose} closeButton={false}>
                      <YousignIframe operation={operation} commande={commande} external_id={external_id} external_type={isAcheteur ? "operation" : "commande"} onClose={handleClose} />
                    </Modal>
                    <Button type="button" onClick={handleClick} value="Signer le marché" />
                  </div>
                )} */}

              {/* NOUVELLE LOGIQUE DE SIGNATURE SIMPLE */}
              {/* Signature côté acheteur (statut 7 = À signer par acheteur) */}
              {isAcheteur && operationStatus === 7 && user?.id.toString() === operation?.acheteur_signataire?.id_user?.toString() && (
                <div className="signature-pending">
                  <h4>Signature du marché</h4>
                  <p>Vous pouvez maintenant signer ce marché côté organisme.</p>
                  <Button
                    type="button"
                    onClick={() => handleSignPublic(operation)}
                    value={signing ? "Signature en cours..." : "Signer le marché"}
                    disabled={signing}
                  />
                </div>
              )}

              {isAcheteur && operationStatus === 13 && (
                <div className="signature-pending">

                  {/* Section 1: Prévisualisation des documents */}
                  <div className="preview-section">
                    <h5>Prévisualisation du marché subséquent</h5>

                    {/* Iframe de prévisualisation du PDF MS */}
                    <div className="ms-pdf-preview" style={{
                      marginTop: '1rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      backgroundColor: '#f5f5f5'
                    }}>
                      {loadingMsPdf ? (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>
                          <p>Chargement du document PDF...</p>
                        </div>
                      ) : msPdfBlobUrl ? (
                        <iframe
                          src={msPdfBlobUrl}
                          title="Prévisualisation du Marché Subséquent"
                          style={{ width: '100%', height: '600px', border: 'none' }}
                        />
                      ) : (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                          <p>Le document PDF du marché subséquent n'est pas encore disponible.</p>
                          <p style={{ fontSize: '0.9em', marginTop: '0.5rem' }}>
                            Il sera généré lors de la validation de l'opération.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 2: Si l'utilisateur EST un acheteur signataire - bouton direct */}
                  {isAcheteurSignataire() && (
                    <div className="signature-direct-section" style={{ marginTop: '1.5rem' }}>
                      <Button
                        variant='primary'
                        size='small'
                        value={signing ? "Signature et envoi en cours..." : "Signer et notifier le retenu"}
                        className='send-to-presta-button'
                        onClick={() => handleSendToPrestataire(operation)}
                        disabled={signing}
                      />
                    </div>
                  )}

                  {/* Section 3: Si l'utilisateur est un acheteur simple (non signataire) - demande par mail */}
                  {isAcheteurSimple() && (
                    <div className="signature-request-section" style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                      <h5>Demande de signature</h5>
                      <p>
                        Vous n'êtes pas autorisé à signer ce marché.
                        Vous pouvez envoyer une demande de signature par email à un signataire autorisé de votre organisme.
                      </p>

                      <div className="signature-request-form" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                        <div>
                          <label htmlFor="signataire-select" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Envoyer la demande de signature à :</label>
                          {loadingSignataires ? (
                            <p>Chargement des signataires...</p>
                          ) : (
                            <select
                              id="signataire-select"
                              className="signataire-select"
                              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                              value={selectedSignataireId || ''}
                              onChange={(e) => setSelectedSignataireId(e.target.value ? parseInt(e.target.value, 10) : null)}
                            >
                              <option value="">-- Sélectionner un signataire --</option>
                              {acheteurSignataires.map((sig) => (
                                <option key={sig.id} value={sig.id}>
                                  {sig.prenom} {sig.nom} ({sig.type_signataire === 'responsable_achat' ? 'Acheteur signataire admin' : 'Acheteur signataire'}) - {sig.email_contact}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>

                        <Button
                          variant='primary'
                          size='small'
                          value={sendingRequest ? "Envoi en cours..." : "Envoyer la demande"}
                          className='request-signature-button'
                          onClick={handleRequestSignature}
                          disabled={sendingRequest || !selectedSignataireId}
                        />
                      </div>

                      {/* Historique des demandes de signature */}
                      {operationData?.historique?.filter(h => h.prefix === 'Demande de signature').length > 0 && (
                        <div className="signature-requests-history" style={{
                          marginTop: '2rem',
                          padding: '1.5rem',
                          backgroundColor: '#d4edda',
                          borderRadius: '12px',
                          borderLeft: '6px solid #28a745'
                        }}>
                          <h5 style={{ color: '#155724', marginBottom: '1.25rem', fontWeight: '700', fontSize: '1.25rem' }}>
                            📧 Historique des demandes de signature
                          </h5>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {operationData.historique
                              .filter(h => h.prefix === 'Demande de signature')
                              .map((entry, index) => (
                                <div key={index} style={{
                                  padding: '1rem 1.25rem',
                                  backgroundColor: 'rgba(255,255,255,0.6)',
                                  borderRadius: '8px',
                                  fontSize: '1.1rem',
                                  color: '#155724'
                                }}>
                                  <div style={{ fontWeight: '600', marginBottom: '0.5rem', lineHeight: '1.4' }}>{entry.label}</div>
                                  <div style={{ fontSize: '1rem', opacity: 0.85 }}>
                                    {new Date(entry.created_at).toLocaleDateString('fr-FR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Section 4: Notification aux non retenus (si plusieurs prestataires) */}
                  {operationData?.commandes &&
                    operationData.commandes.filter((cmd: any) =>
                      cmd.id !== operation?.id_commande &&
                      cmd.id_status !== 12 &&
                      cmd.id_user !== null &&
                      cmd.type_compte !== null
                    ).length > 0 && (
                      <div className="non-retenus-section" style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '8px', borderLeft: '4px solid #ffc107' }}>
                        <h5 style={{ marginBottom: '0.5rem', color: '#856404' }}>Notification au(x) non retenu(s)</h5>
                        <p style={{ color: '#856404', marginBottom: '1rem' }}>
                          {operationData.commandes.filter((cmd: any) =>
                            cmd.id !== operation?.id_commande &&
                            cmd.id_status !== 6 &&
                            cmd.id_status !== 12
                          ).length} prestataire(s) non retenu(s) à notifier.
                        </p>
                        <div className="non-retenus-list">
                          {operationData.commandes
                            .filter((cmd: any) =>
                              cmd.id !== operation?.id_commande && // Exclure le retenu
                              cmd.id_status !== 12 // Exclure les commandes refusées
                            )
                            .map((cmd: any) => (
                              <div key={cmd.id} className="non-retenu-item" style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '0.75rem',
                                backgroundColor: '#fff',
                                borderRadius: '4px',
                                marginBottom: '0.5rem',
                                border: '1px solid #e0e0e0'
                              }}>
                                <div>
                                  <strong>{cmd.contrat?.entite?.nom || 'Prestataire'}</strong>
                                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                                    {cmd.contrat?.entite?.gestionnaire_entite?.email_contact || cmd.contrat?.entite?.interlocuteur?.email_contact || 'Email non disponible'}
                                  </p>
                                </div>
                                {isNotified(cmd) ? (
                                  <span style={{ color: '#28a745', fontWeight: '500', fontSize: '0.9rem' }}>
                                    ✓ Notifié
                                  </span>
                                ) : (
                                  <Button
                                    variant='secondary'
                                    size='small'
                                    value="Notifier"
                                    onClick={() => handleNotifyPrestataire(operation?.id, cmd.id, operation?.id_commande)}
                                  />
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                </div>
              )}

              {isAcheteur && operationStatus === 9 && (
                <div className="signature-pending">
                  <h4>En attente de la signature du prestataire</h4>
                  <p>
                    Le prestataire doit signer le marché avant que vous puissiez apposer votre signature
                    électronique.
                  </p>
                  <p>
                    Vous serez automatiquement notifié dès que le prestataire aura signé. Vous pourrez alors
                    revenir ici pour compléter votre signature.
                  </p>
                </div>
              )}

              {/* En attente de signature prestataire (l'acheteur a déjà signé) - statut 10 = À signer */}
              {(isPresta && (commandeStatus === 8 || commandeStatus === 10)) && (
                <div className="signature-prestataire-en-cours">
                  <div className="signature-pending">
                    <h4>Le marché est prêt pour votre signature</h4>
                    <p>L'acheteur a validé et signé le marché. C'est maintenant à vous de signer pour finaliser le marché subséquent.</p>
                    <Button
                      type="button"
                      onClick={() => handleSignPrivate(commande?.operation || operation, commande)}
                      value={signing ? "Signature en cours..." : "Signer le marché"}
                      disabled={signing}
                    />
                  </div>
                </div>

              )}

              {/* Message pour le prestataire qui n'est pas le signataire désigné */}
              {isPresta && commande?.prestataire_signataire?.id_user?.toString() !== user?.id?.toString() && commandeStatus !== 11 && (
                <div className="signature-pending">
                  <h4>Vous n'êtes pas le signataire désigné pour ce marché</h4>
                  <p>Seul le signataire défini <span className='signataire'>({commande?.prestataire_signataire ?
                    `${commande.prestataire_signataire?.nom || ''} ${commande.prestataire_signataire?.prenom || ''} - ${commande.prestataire_signataire?.email_contact || ''}`
                    : 'Non défini'})</span> peut signer électroniquement ce document.</p>
                </div>
              )}

              {/* Marché signé et validé par les deux parties */}
              {((isAcheteur && operationStatus === 11) || (isPresta && commandeStatus === 11)) && (
                <div className="signature-complete">
                  <div className="signature-success">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    <h4>Marché signé électroniquement</h4>
                    <p style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                      Le marché a été signé par toutes les parties le{" "}
                      {isAcheteur
                        ? formatDate(operation?.updated_at)
                        : formatDate(commande?.updated_at)}
                    </p>
                  </div>
                  <div className="document-actions">
                    <Button
                      value="Télécharger le marché subséquent signé"
                      variant="secondary"
                      icon={<PdfIcon />}
                      onClick={() => handleDownloadSigned(operation ?? operationData)}
                    />
                    {/* <Button
                      value="Télécharger les attestations de signature"
                      variant="secondary"
                      icon={<PdfIcon />}
                      onClick={() => handleDownloadAuditTrail(operation ?? operationData)}
                    /> */}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section>
            <h3 className='section-header'>Coordonnées</h3>
            <div className="section-content coordinates-section">
              <Coordonnees
                prestataire={selectedPrestataire}
                commande={commande}
                operation={operationData || operation}
                isAcheteur={isAcheteur}
                isPresta={isPresta}
                showSignataire
              />
            </div>
          </section>

        </div>


      </div>
      <div className='print-button-container'>
        <PrintButton
          targetRef={printRef}
          fileName={`${operation?.numero || commande?.operation?.numero} - Notifications et signatures`}
          suiviHeaderProps={{
            titre: commande?.operation?.nom || operation?.nom,
            sousTitre: getSousTitre(), // fonction disponible dans le parent
            subtitle: getSubtitle(),   // fonction disponible dans le parent
            status: getDisplayStatus(),
            statusColor: getStatusColor(getDisplayStatus()),
            delais: operation?.expire_at || commande?.operation?.expire_at,
            backgroundImage: getLotImage(commande?.operation?.lot_technique?.nom || operation?.lot_technique?.nom),
          }}
        />
      </div>
    </>
  );
};

export default Marche;